import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Empty, Eyebrow, PageHeader, Panel, Pill, Tag } from '../ui/kit';
import { MARKS } from '../../lib/marks';
import { buildDnsRecords, normalizeDomainInput, type DnsRecord } from '../../lib/dns';
import {
  customDomainService,
  customDomainErrorMessage,
  type CustomDomainState,
} from '../../services/customDomainService';
import { messageService } from '../../services/messageService';

/**
 * Custom Domain setup page.
 *
 * Wired to the real conversion path (fnd-bmi.1): POST/GET/DELETE
 * /api/domain/connect, which creates the Cloudflare for SaaS custom hostname,
 * writes the tenant-map KV entry the R2 front Worker reads, and reports what
 * Cloudflare actually says about DNS and the certificate.
 *
 * Four states, in the order a client meets them:
 *   - Coming soon  — the owner has not enabled Cloudflare for SaaS yet. We say
 *                    so plainly and offer to do it by hand (a real message, not
 *                    a mailto), showing the records we *would* ask for. Never a
 *                    form that goes nowhere.
 *   - "I own a domain" — connect, then the DNS records and a live status poll.
 *   - Connected    — Cloudflare reports the apex active over HTTPS.
 *   - "Buy a new one"  — availability search (/api/domain/check-availability).
 *
 * Nothing on this page invents a status: `pending_dns` / `pending_ssl` /
 * `active` / `failed` all come from Cloudflare via the backend.
 */

interface WebsiteData {
  id: string;
  domain: string;
  currentActiveUrl?: string;
  netlifyUrl?: string;
  websiteStatus?: 'temporary' | 'transitioning' | 'live';
  status: string;
  businessName?: string;
}

interface DomainAvailability {
  domain: string;
  available: boolean;
  price?: number;
  registrar?: string;
  error?: string;
}

type Tab = 'own' | 'buy';

/** How often the "waiting for DNS" view re-asks Cloudflare. */
const STATUS_POLL_MS = 15_000;

// ---- Icons -----------------------------------------------------------------
const HomeIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
  </svg>
);
const ChevronRightIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);
const GlobeIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
  </svg>
);
const CopyIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <rect x="9" y="9" width="11" height="11" rx="2" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);
const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);
const RefreshIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M3.985 14.652H-.007m4.992 0v4.992m0-4.992 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.183m0-4.993v4.992" />
  </svg>
);

// ---- DNS record row --------------------------------------------------------
const DnsRow: React.FC<{ record: DnsRecord }> = ({ record }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(record.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="border-t border-zinc-200 py-2.5">
      <div className="grid grid-cols-[64px_1fr_1.4fr_auto] gap-3 items-center text-[13px]">
        <span className="font-mono text-muted">{record.type}</span>
        <span className="font-mono truncate">{record.host}</span>
        <span className="font-mono truncate">{record.value}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-eyebrow text-muted transition-colors hover:bg-zinc-100 hover:text-primary"
        >
          {copied ? <CheckIcon className="w-3.5 h-3.5" /> : <CopyIcon className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {/* The apex caveat matters more than it looks — most registrars refuse a
          literal CNAME on the root and hide the equivalent under another name. */}
      {record.note && <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-500">{record.note}</p>}
    </div>
  );
};

const StatusChip: React.FC<{ tone: 'amber' | 'blue' | 'green' | 'red'; label: string }> = ({ tone, label }) => (
  <Tag tone={tone === 'green' ? 'live' : tone === 'amber' || tone === 'red' ? 'warn' : 'info'} dot>
    {label}
  </Tag>
);

/** Hosts we hand out ourselves — anything else the client pointed here is theirs. */
const PLATFORM_HOST = /(^|\.)(foundlio\.(com|app)|netlify\.app|pages\.dev|vercel\.app|workers\.dev)$/i;

const hostOf = (url?: string | null): string =>
  (url || '')
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '')
    .toLowerCase();

/** One health row in the connected state. */
const HealthRow: React.FC<{ label: string; detail: string; ok?: boolean }> = ({ label, detail, ok = true }) => (
  <Panel className="p-3.5">
    <Eyebrow>{label}</Eyebrow>
    <div className="mt-1.5">
      <StatusChip tone={ok ? 'green' : 'amber'} label={ok ? 'OK' : 'Pending'} />
    </div>
    <div className="mt-2 font-mono text-[11px] text-zinc-400">{detail}</div>
  </Panel>
);

/** The client-facing sentence for each Cloudflare state. */
const STATUS_COPY: Record<string, { label: string; tone: 'amber' | 'blue' | 'green' | 'red'; detail: string }> = {
  pending_dns: {
    label: 'Awaiting DNS',
    tone: 'blue',
    detail: 'Add the records below at your registrar. We check every few seconds.',
  },
  pending_ssl: {
    label: 'Issuing certificate',
    tone: 'blue',
    detail: 'Your records are visible to us. The HTTPS certificate usually lands within a few minutes.',
  },
  active: { label: 'Live', tone: 'green', detail: 'Your domain is connected and serving over HTTPS.' },
  failed: {
    label: 'Needs attention',
    tone: 'red',
    detail: 'We could not finish setting this up. Check the records below, then try again.',
  },
};

// ---- Page ------------------------------------------------------------------
const CustomDomainPage: React.FC = () => {
  // Works both as a website-scoped route (/websites/:id/domain) and as a
  // standalone Settings page (/account/domain) with no id in the URL.
  const { id: routeWebsiteId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [pickedId, setPickedId] = useState<string | undefined>(undefined);
  const [tab, setTab] = useState<Tab>('own');

  // Connect-existing flow state
  const [ownDomain, setOwnDomain] = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);

  // Set when a client with a live domain deliberately opens the add flow again.
  const [addingAnother, setAddingAnother] = useState(false);

  // Buy flow state
  const [buyTerm, setBuyTerm] = useState('');
  const [buyResult, setBuyResult] = useState<DomainAvailability | null>(null);
  const [buyLoading, setBuyLoading] = useState(false);

  // "Coming soon" hand-off — a real message to the team, not a mailto.
  const [wantedDomain, setWantedDomain] = useState('');
  const [wantedSent, setWantedSent] = useState(false);

  // When no id is in the route, resolve the user's website(s) ourselves.
  const { data: websiteList, isLoading: listLoading } = useQuery<WebsiteData[]>({
    queryKey: ['websites-for-domain'],
    queryFn: async () => {
      const response = await axios.get('/api/websites');
      return response.data.data.websites;
    },
    enabled: !routeWebsiteId,
    retry: 1,
  });

  const websiteId = routeWebsiteId || pickedId || websiteList?.[0]?.id;

  const { data: fetchedWebsite, isLoading: detailLoading } = useQuery<WebsiteData>({
    queryKey: ['website-domain', websiteId],
    queryFn: async () => {
      const response = await axios.get(`/api/websites/${websiteId}`);
      return response.data.data.website;
    },
    enabled: !!websiteId,
    retry: 1,
  });

  const website = fetchedWebsite;

  /**
   * The authority on what is connected and how far along it is. Polls only
   * while something is genuinely in flight — an active or absent domain has
   * nothing to watch.
   */
  const { data: domainState, isLoading: domainLoading, isFetching: domainFetching, refetch } =
    useQuery<CustomDomainState>({
      queryKey: ['custom-domain', websiteId],
      queryFn: () => customDomainService.status(websiteId),
      enabled: !!websiteId,
      retry: 1,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === 'pending_dns' || status === 'pending_ssl' ? STATUS_POLL_MS : false;
      },
    });

  const featureEnabled = domainState?.enabled ?? false;
  const isLoading = (routeWebsiteId ? detailLoading : listLoading || detailLoading) || domainLoading;

  const connectMutation = useMutation({
    mutationFn: (domain: string) => customDomainService.connect(domain, websiteId),
    onSuccess: (state) => {
      setConnectError(null);
      queryClient.setQueryData(['custom-domain', websiteId], state);
    },
    onError: (error) => {
      setConnectError(customDomainErrorMessage(error) ?? 'Could not connect that domain. Please try again.');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => customDomainService.disconnect(websiteId),
    onSuccess: () => {
      setAddingAnother(false);
      setOwnDomain('');
      void queryClient.invalidateQueries({ queryKey: ['custom-domain', websiteId] });
    },
  });

  const tempUrl = website?.currentActiveUrl || website?.netlifyUrl || website?.domain || '';

  /**
   * A domain counts as connected only when Cloudflare says the apex is active.
   * Before this page had a backend it guessed from `currentActiveUrl`; that
   * guess survives solely as a fallback for accounts whose domain predates the
   * conversion flow and therefore has no record on the new columns.
   */
  const activeHost = hostOf(website?.currentActiveUrl);
  const legacyConnectedDomain =
    website?.websiteStatus === 'live' && activeHost && !PLATFORM_HOST.test(activeHost) ? activeHost : null;
  const connectedDomain =
    domainState?.status === 'active' ? domainState.domain : domainState?.domain ? null : legacyConnectedDomain;

  /** The foundlio address the site keeps, shown as a fallback, not as the answer. */
  const platformHost = [website?.netlifyUrl, website?.domain]
    .map(hostOf)
    .find((host) => host && PLATFORM_HOST.test(host));

  // Records: whatever the backend returned for the connected domain, otherwise
  // a preview built from what the client is typing.
  const dnsRecords: DnsRecord[] = useMemo(() => {
    if (domainState?.records?.length && !ownDomain) return domainState.records;
    return buildDnsRecords(ownDomain || domainState?.domain || 'yourdomain.com', domainState?.fallbackOrigin);
  }, [domainState?.records, domainState?.domain, domainState?.fallbackOrigin, ownDomain]);

  // Once a domain is connected, the input should show it rather than sitting empty.
  useEffect(() => {
    if (domainState?.domain && !ownDomain && !addingAnother) setOwnDomain(domainState.domain);
  }, [domainState?.domain, addingAnother]); // eslint-disable-line react-hooks/exhaustive-deps

  const inFlight = domainState?.status === 'pending_dns' || domainState?.status === 'pending_ssl';
  const statusCopy = domainState?.status ? STATUS_COPY[domainState.status] : null;

  const handleConnect = () => {
    const domain = normalizeDomainInput(ownDomain);
    if (!domain) return;
    setConnectError(null);
    connectMutation.mutate(domain);
  };

  const handleSearch = async () => {
    const term = buyTerm.trim();
    if (!term) return;
    setBuyLoading(true);
    setBuyResult(null);
    try {
      const res = await axios.post('/api/domain/check-availability', { domain: term });
      setBuyResult(res.data.data);
    } catch {
      setBuyResult({ domain: term, available: false, error: 'Unable to check availability. Please try again.' });
    } finally {
      setBuyLoading(false);
    }
  };

  const handleTellUs = async () => {
    const domain = normalizeDomainInput(wantedDomain);
    if (!domain) return;
    try {
      await messageService.send(
        `I'd like my site on my own domain: ${domain}. Please set it up when custom domains are ready.`
      );
      setWantedSent(true);
    } catch {
      /* the global error interceptor already surfaced this */
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // No website to configure (Settings tab for an account with no sites yet).
  if (!websiteId) {
    return (
      <div className="max-w-3xl">
        <PageHeader eyebrow="Domain" title="Custom domain" />
        <Empty
          mark={MARKS.domain}
          eyebrow="Domain"
          title="No website yet"
          description="Once your site is live you can point your own domain at it here, or buy a new one."
          hint="Hosting and the SSL certificate are included either way."
        />
      </div>
    );
  }

  const showPicker = !routeWebsiteId && (websiteList?.length ?? 0) > 1;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <Link to="/dashboard" className="text-muted hover:text-primary transition-colors">
              <HomeIcon className="w-5 h-5" />
            </Link>
          </li>
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-4 h-4 text-zinc-300" />
            <Link to="/dashboard/websites" className="text-muted hover:text-primary transition-colors">
              Websites
            </Link>
          </li>
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-4 h-4 text-zinc-300" />
            <span className="text-primary font-medium">Custom domain</span>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <PageHeader
        eyebrow="Domain"
        title="Custom domain"
        description={
          connectedDomain && !addingAnother
            ? `${website?.businessName || 'Your website'} is serving on your own domain`
            : `Connect a domain to ${website?.businessName || 'your website'}`
        }
        // When a domain is live the panel below already states the address —
        // repeating it in the header would say the same thing twice.
        actions={!connectedDomain && tempUrl && (
          <div className="flex items-center gap-2 text-xs bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
            <GlobeIcon className="w-4 h-4 text-muted" />
            <span className="font-mono text-muted">{tempUrl.replace(/^https?:\/\//, '')}</span>
            {website?.websiteStatus !== 'live' && (
              <Tag tone="warn">Temporary</Tag>
            )}
          </div>
        )}
      />

      {/* Website picker — only on the standalone Settings page with >1 site */}
      {showPicker && (
        <div>
          <Eyebrow className="mb-1.5">Website</Eyebrow>
          <select
            value={websiteId}
            onChange={(e) => {
              setPickedId(e.target.value);
              setOwnDomain('');
              setConnectError(null);
            }}
            className="input-field w-full sm:w-72"
          >
            {websiteList?.map((w) => (
              <option key={w.id} value={w.id}>
                {w.businessName || w.domain}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ---- Coming soon (feature not enabled yet) --------------------------- */}
      {!featureEnabled && (
        <div className="space-y-4">
          <Panel className="p-5">
            <div className="flex flex-wrap items-center gap-2">
              <Eyebrow>Your own domain</Eyebrow>
              <Tag tone="info">Coming soon</Tag>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Self-serve domain connection is not switched on yet. Tell us the domain you want and we
              will point it at your site by hand — same result, same day, no records for you to copy.
            </p>
            {wantedSent ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700">
                <CheckIcon className="w-4 h-4" />
                Got it — we will be in touch on your message thread.
              </div>
            ) : (
              <div className="mt-4 flex gap-2 items-center">
                <input
                  value={wantedDomain}
                  onChange={(e) => setWantedDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void handleTellUs()}
                  placeholder="smithplumbing.com"
                  className="input-field font-mono"
                />
                <Pill onClick={() => void handleTellUs()} className="whitespace-nowrap">
                  Tell us
                </Pill>
              </div>
            )}
          </Panel>

          <Panel className="p-5">
            <Eyebrow>What we will ask for, when it opens up</Eyebrow>
            {buildDnsRecords(wantedDomain || 'yourdomain.com', domainState?.fallbackOrigin).map((r, i) => (
              <DnsRow key={i} record={r} />
            ))}
          </Panel>
        </div>
      )}

      {/* ---- Already connected ---------------------------------------------- */}
      {featureEnabled && connectedDomain && !addingAnother && (
        <div className="space-y-4">
          <Panel className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <Eyebrow className="mb-2">Primary domain</Eyebrow>
                <div className="font-mono text-[20px] font-medium text-primary truncate">
                  {connectedDomain}
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <StatusChip tone="green" label="Live" />
                  <span className="text-muted">Connected · HTTPS active</span>
                </div>
              </div>
              <a
                href={`https://${connectedDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-zinc-200 px-3.5 py-2 text-[13px] text-muted transition-colors hover:bg-zinc-100 hover:text-primary"
              >
                <GlobeIcon className="w-4 h-4" />
                Visit site
              </a>
            </div>
          </Panel>

          {/* Per-hostname health, straight from Cloudflare — no invented rows. */}
          <div className="grid gap-3 sm:grid-cols-3">
            {domainState?.hostnames.map((h) => (
              <HealthRow
                key={h.hostname}
                label={h.hostname.startsWith('www.') ? 'www' : 'Root domain'}
                detail={h.detail ?? `CNAME → ${domainState.fallbackOrigin}`}
                ok={h.status === 'active'}
              />
            ))}
            <HealthRow label="SSL certificate" detail="Auto-renewing" />
          </div>

          {platformHost && (
            <Panel className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <Eyebrow className="mb-1">Foundlio address</Eyebrow>
                  <span className="font-mono text-[13px] text-muted">{platformHost}</span>
                </div>
                <span className="text-[13px] text-zinc-400">
                  Still works — it serves the same site.
                </span>
              </div>
            </Panel>
          )}

          {/*
            Nothing here needs doing, so every action is quiet: a re-check for
            when something looks off, the second-domain path as a footnote, and
            disconnect last of all.
          */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Pill onClick={() => void refetch()} tone="outline" disabled={domainFetching}>
              <RefreshIcon className={`w-4 h-4 ${domainFetching ? 'animate-spin' : ''}`} />
              {domainFetching ? 'Checking…' : 'Re-check DNS'}
            </Pill>
            <button
              onClick={() => {
                setAddingAnother(true);
                setTab('own');
                setOwnDomain('');
                setConnectError(null);
              }}
              className="text-[13px] text-muted underline-offset-4 transition-colors hover:text-primary hover:underline"
            >
              Connect a different domain
            </button>
            <button
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              className="ml-auto text-[13px] text-zinc-400 underline-offset-4 transition-colors hover:text-primary hover:underline"
            >
              {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}

      {/* ---- Setup / add-another -------------------------------------------- */}
      {featureEnabled && (!connectedDomain || addingAnother) && (
      <>
      {addingAnother && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white/60 px-4 py-3">
          <span className="text-[13px] text-muted">
            Connecting a different domain replaces{' '}
            <span className="font-mono text-primary">{connectedDomain}</span> as your address.
          </span>
          <button
            onClick={() => {
              setAddingAnother(false);
              setOwnDomain('');
              setConnectError(null);
            }}
            className="ml-auto text-[13px] text-muted underline-offset-4 transition-colors hover:text-primary hover:underline"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="inline-flex gap-2">
        {(['own', 'buy'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors ${
              tab === t
                ? 'bg-primary text-white'
                : 'bg-white text-primary ring-1 ring-zinc-300 hover:bg-white'
            }`}
          >
            {t === 'own' ? 'I own a domain' : 'Buy a new one'}
          </button>
        ))}
      </div>

      {/* ---- Connect existing ------------------------------------------------ */}
      {tab === 'own' && (
        <div className="space-y-4">
          <Panel className="p-5">
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <input
                  value={ownDomain}
                  onChange={(e) => {
                    setOwnDomain(e.target.value);
                    setConnectError(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                  placeholder="smithplumbing.com"
                  className="input-field font-mono"
                />
              </div>
              <Pill
                onClick={handleConnect}
                disabled={connectMutation.isPending || !normalizeDomainInput(ownDomain)}
                className="whitespace-nowrap"
              >
                {connectMutation.isPending ? 'Connecting…' : 'Connect domain'}
              </Pill>
            </div>

            {connectError && (
              <p className="mt-3 text-[13px] text-red-700">{connectError}</p>
            )}

            {statusCopy && domainState?.domain && (
              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <StatusChip tone={statusCopy.tone} label={statusCopy.label} />
                  <span className="text-muted">{statusCopy.detail}</span>
                </div>
                {/* Cloudflare's own words when something is wrong — a client
                    with "CNAME not found" can act; "failed" alone cannot. */}
                {domainState.detail && (
                  <p className="font-mono text-[12px] text-zinc-500">{domainState.detail}</p>
                )}
              </div>
            )}
          </Panel>

          {domainState?.domain && domainState.status !== 'active' && (
            <>
              {domainState.dnsManaged ? (
                <Panel className="p-5">
                  <Eyebrow>DNS</Eyebrow>
                  <p className="mt-2 text-sm leading-relaxed text-muted">
                    This is a domain we registered for you, so we have already pointed it at your
                    site. Nothing for you to do — the certificate follows automatically.
                  </p>
                </Panel>
              ) : (
                <Panel className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <Eyebrow>DNS records to add</Eyebrow>
                    <span className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      at your registrar
                    </span>
                  </div>
                  {dnsRecords.map((r, i) => (
                    <DnsRow key={i} record={r} />
                  ))}
                  <div className="flex flex-wrap gap-1.5 items-center mt-3 pt-3 border-t border-zinc-200">
                    <span className="text-xs text-zinc-400">Delete any existing A or CNAME on the same host first.</span>
                  </div>
                </Panel>
              )}

              <div className="grid grid-cols-2 gap-3">
                <HealthRow
                  label="DNS propagation"
                  detail={domainState.status === 'pending_dns' ? 'Waiting for your records' : 'Records visible'}
                  ok={domainState.status !== 'pending_dns'}
                />
                <HealthRow
                  label="SSL certificate"
                  detail={domainState.status === 'pending_ssl' ? 'Provisioning' : 'Queued'}
                  ok={false}
                />
              </div>

              <Pill onClick={() => void refetch()} disabled={domainFetching} className="w-full">
                <RefreshIcon className={`w-4 h-4 ${domainFetching || inFlight ? 'animate-spin' : ''}`} />
                {domainFetching ? 'Checking…' : 'Check now'}
              </Pill>
            </>
          )}
        </div>
      )}

      {/* ---- Buy new --------------------------------------------------------- */}
      {tab === 'buy' && (
        <div className="space-y-4">
          <Panel className="p-5">
            <Eyebrow className="mb-2">Search for an available domain</Eyebrow>
            <div className="flex gap-2 items-center">
              <div className="relative flex-1 flex items-center">
                <input
                  value={buyTerm}
                  onChange={(e) => setBuyTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="smithplumbing"
                  className="input-field font-mono pr-12"
                />
                <span className="absolute right-3 text-xs text-zinc-400 font-mono pointer-events-none">.com</span>
              </div>
              <Pill
                onClick={handleSearch}
                disabled={buyLoading}
                tone="outline"
              >
                {buyLoading ? 'Searching…' : 'Search'}
              </Pill>
            </div>
          </Panel>

          {buyResult && buyResult.available && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <CheckIcon className="w-5 h-5 text-emerald-700" />
                <div>
                  <div className="font-mono text-[13px] font-medium text-emerald-800">{buyResult.domain}</div>
                  <div className="text-[13px] text-emerald-700">
                    Available · ${buyResult.price ?? 12} / year · auto-renews
                  </div>
                </div>
              </div>
              <Pill className="whitespace-nowrap">
                Register &amp; connect
              </Pill>
            </div>
          )}

          {buyResult && !buyResult.available && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {buyResult.error || `${buyResult.domain} is already taken.`}
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-zinc-400">
            <span>We register it, point DNS and provision SSL — no records for you to copy.</span>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default CustomDomainPage;
